import { infiniteQueryOptions, useInfiniteQuery } from "@tanstack/react-query";
import { api } from "@/lib/http.ts";
import { useCallback, useMemo } from "react";

import {
  type ArtifactLibraryItem,
  type ArtifactLibraryResp,
  type ArtifactLibrarySharingId,
  DEFAULT_LIBRARY_SORT,
  type LibrarySort,
} from "@/types/artifactLibrary.ts";

const ARTIFACT_LIBRARY_PAGE_SIZE = 30;

export type ArtifactLibraryParams = {
  /** Free-text query; empty → recency-ordered listing. */
  q?: string;
  /** Artifact types behind the active chip; empty/undefined → all Library types. */
  types?: readonly string[];
  /**
   * How share grants figure in the results. `all` fuses shared items in
   * alongside the caller's own; `with-me` returns them alone. Undefined is not
   * a synonym for `all` — it is the pre-sharing contract, which is what a
   * gated-out client sends.
   */
  sharing?: ArtifactLibrarySharingId;
  /** Result order; undefined → the server's default (relevance). */
  sort?: LibrarySort;
};

export const artifactLibraryQueries = {
  all: () => ["artifactLibrary"] as const,
  list: ({ q, types, sharing, sort }: ArtifactLibraryParams = {}) =>
    infiniteQueryOptions({
      // `sort` is part of the key, so switching order refetches from offset 0
      // rather than appending a differently-ordered page to the loaded ones.
      queryKey: [...artifactLibraryQueries.all(), { q, types, sharing, sort }] as const,
      queryFn: async ({ pageParam = 0, queryKey, signal }) => {
        const [, options] = queryKey;
        const queryParams = new URLSearchParams();
        if (options.q) {
          queryParams.set("q", options.q);
        }
        if (options.types && options.types.length > 0) {
          queryParams.set("type", options.types.join(","));
        }
        if (options.sharing) {
          queryParams.set("sharing", options.sharing);
        }
        // Only when it differs from the default, so the common request is byte
        // for byte the one every client sent before this param existed.
        if (options.sort && options.sort !== DEFAULT_LIBRARY_SORT) {
          queryParams.set("sort", options.sort);
        }
        queryParams.set("limit", ARTIFACT_LIBRARY_PAGE_SIZE.toString());
        queryParams.set("offset", pageParam.toString());

        const response = await api.get<ArtifactLibraryResp>(
          `/api/a/artifact-library?${queryParams.toString()}`,
          { signal },
        );
        return response.data;
      },
      initialPageParam: 0,
      getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.nextOffset : undefined),
    }),
};

/**
 * Infinite-scrolling Artifact Library list. Returns the artifacts flattened
 * across loaded pages plus the controls a list view needs (load-more, loading
 * flags). Filtered by free text (`q`) and/or the active chip's artifact `types`,
 * ordered by `sort`.
 */
export const useArtifactLibrary = ({ q, types, sharing, sort }: ArtifactLibraryParams = {}) => {
  const queryOptions = useMemo(
    () => artifactLibraryQueries.list({ q, types, sharing, sort }),
    [q, types, sharing, sort],
  );

  const { data, isLoading, error, fetchNextPage, hasNextPage, isFetching, isFetchingNextPage } =
    useInfiniteQuery(queryOptions);

  const items: ArtifactLibraryItem[] = useMemo(
    () => data?.pages.flatMap((page) => page.items) ?? [],
    [data],
  );

  const loadMore = useCallback(() => {
    fetchNextPage();
  }, [fetchNextPage]);

  return {
    items,
    isLoading,
    error,
    hasNextPage,
    loadMore,
    isFetching,
    isFetchingNextPage,
  };
};
